import React from 'react';
import { CarIcon, ClockIcon, TrashIcon, PlayIcon, CheckIcon, UserIcon, PhoneIcon, BanknoteIcon, TicketIcon } from 'lucide-react';
interface Vehicle {
  id: string;
  ticketNumber: string;
  name: string;
  phone: string;
  plateNumber: string;
  vehicleType: string;
  category: string;
  pumpNumber: number;
  serviceType: string;
  estimatedPrice: number;
  addedAt: Date;
  status: 'waiting' | 'in-service' | 'completed';
  serviceStartedAt?: Date;
  addedBy: string;
  addedByName: string;
  lastModifiedBy?: string;
  lastModifiedByName?: string;
}
interface QueueItemProps {
  vehicle: Vehicle;
  position: number;
  currentTime: Date;
  onUpdateStatus: (id: string, status: Vehicle['status']) => void;
  onRemove: (id: string) => void;
  canUpdateStatus: boolean;
  canRemove: boolean;
}
export function QueueItem({
  vehicle,
  position,
  currentTime,
  onUpdateStatus,
  onRemove,
  canUpdateStatus,
  canRemove
}: QueueItemProps) {
  const waitTime = Math.floor((currentTime.getTime() - vehicle.addedAt.getTime()) / 60000);
  const statusColors = {
    waiting: 'bg-yellow-100 border-yellow-300 text-yellow-800',
    'in-service': 'bg-green-100 border-green-300 text-green-800',
    completed: 'bg-gray-100 border-gray-300 text-gray-800'
  };
  const getStatusLabel = () => {
    if (vehicle.status === 'waiting') return 'En Attente';
    if (vehicle.status === 'completed') return 'Terminé';
    if (vehicle.status === 'in-service' && vehicle.serviceStartedAt) {
      const serviceTime = vehicle.serviceStartedAt.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
      return `Servi (${serviceTime})`;
    }
    return 'En Service';
  };
  const statusLabels = {
    waiting: 'En Attente',
    'in-service': 'En Service',
    completed: 'Terminé'
  };
  const categoryColors: Record<string, string> = {
    Particulier: 'bg-blue-100 text-blue-800',
    Professionnel: 'bg-purple-100 text-purple-800',
    Transport: 'bg-orange-100 text-orange-800',
    Entreprise: 'bg-indigo-100 text-indigo-800',
    VIP: 'bg-yellow-100 text-yellow-800'
  };
  return <div className={`border-2 rounded-xl p-4 transition-all ${statusColors[vehicle.status]}`} data-id="element-398">
      <div className="flex items-center justify-between" data-id="element-399">
        <div className="flex items-center gap-4 flex-1" data-id="element-400">
          {/* Position Badge */}
          <div className="bg-white rounded-lg px-4 py-2 shadow-sm" data-id="element-401">
            <span className="text-2xl font-bold text-gray-900" data-id="element-402">
              #{position}
            </span>
          </div>
          {/* Vehicle Info */}
          <div className="flex-1" data-id="element-403">
            <div className="flex items-center gap-2 mb-2" data-id="element-404">
              <div className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-3 py-1 rounded-lg" data-id="element-405">
                <TicketIcon className="w-4 h-4" data-id="element-406" />
                <span className="font-bold text-sm" data-id="element-407">
                  {vehicle.ticketNumber}
                </span>
              </div>
              <CarIcon className="w-5 h-5" data-id="element-408" />
              <span className="font-bold text-lg" data-id="element-409">{vehicle.plateNumber}</span>
              <span className="text-sm px-2 py-1 bg-white rounded-md" data-id="element-410">
                Pompe {vehicle.pumpNumber}
              </span>
              <span className={`text-xs px-2 py-1 rounded-md font-semibold ${categoryColors[vehicle.category] || 'bg-gray-100 text-gray-800'}`} data-id="element-411">
                {vehicle.category}
              </span>
            </div>
            <div className="flex items-center gap-2 mb-1" data-id="element-412">
              <UserIcon className="w-4 h-4" data-id="element-413" />
              <span className="font-semibold" data-id="element-414">{vehicle.name}</span>
            </div>
            <div className="flex items-center gap-4 text-sm flex-wrap" data-id="element-415">
              <div className="flex items-center gap-1" data-id="element-416">
                <PhoneIcon className="w-4 h-4" data-id="element-417" />
                <span data-id="element-418">{vehicle.phone}</span>
              </div>
              <span data-id="element-419">•</span>
              <span data-id="element-420">{vehicle.vehicleType}</span>
              <span data-id="element-421">•</span>
              <span data-id="element-422">{vehicle.serviceType}</span>
              <span data-id="element-423">•</span>
              <div className="flex items-center gap-1" data-id="element-424">
                <BanknoteIcon className="w-4 h-4" data-id="element-425" />
                <span className="font-semibold" data-id="element-426">
                  {vehicle.estimatedPrice.toLocaleString()} FCFA
                </span>
              </div>
              <span data-id="element-427">•</span>
              <div className="flex items-center gap-1" data-id="element-428">
                <ClockIcon className="w-4 h-4" data-id="element-429" />
                <span data-id="element-430">{waitTime} min</span>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-600 mt-2 flex-wrap" data-id="element-431">
              <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-md" data-id="element-432">
                <UserIcon className="w-3 h-3" data-id="element-433" />
                <span data-id="element-434">
                  Ajouté par:{' '}
                  <span className="font-semibold" data-id="element-435">{vehicle.addedByName}</span>
                </span>
              </div>
              {vehicle.lastModifiedBy && vehicle.lastModifiedByName && <>
                  <span data-id="element-436">•</span>
                  <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded-md" data-id="element-437">
                    <UserIcon className="w-3 h-3" data-id="element-438" />
                    <span data-id="element-439">
                      Modifié par:{' '}
                      <span className="font-semibold" data-id="element-440">
                        {vehicle.lastModifiedByName}
                      </span>
                    </span>
                  </div>
                </>}
            </div>
          </div>
          {/* Status Badge */}
          <div className="px-4 py-2 bg-white rounded-lg font-semibold" data-id="element-441">
            {getStatusLabel()}
          </div>
        </div>
        {/* Actions */}
        <div className="flex items-center gap-2 ml-4" data-id="element-442">
          {vehicle.status === 'waiting' && canUpdateStatus && <button onClick={() => onUpdateStatus(vehicle.id, 'in-service')} className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-lg transition-colors" title="Commencer le service" data-id="element-443">
              <PlayIcon className="w-5 h-5" data-id="element-444" />
            </button>}
          {vehicle.status === 'in-service' && canUpdateStatus && <button onClick={() => onUpdateStatus(vehicle.id, 'completed')} className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg transition-colors" title="Terminer le service" data-id="element-445">
              <CheckIcon className="w-5 h-5" data-id="element-446" />
            </button>}
          {canRemove && <button onClick={() => onRemove(vehicle.id)} className="bg-red-600 hover:bg-red-700 text-white p-3 rounded-lg transition-colors" title="Retirer de la file" data-id="element-447">
              <TrashIcon className="w-5 h-5" data-id="element-448" />
            </button>}
        </div>
      </div>
    </div>;
}